'use client'

import {useEffect, useMemo, useState} from 'react'
import * as XLSX from 'xlsx'

export type InvalidRow = {
  rowNumber: number // 엑셀 데이터 행(보통 2부터)
  key: string | null // 수용가번호
  issues: string[] // 예: ['수용가번호 공란', '필수 공란: 자원번호']
  original?: Record<string, string>
}

export type ValidationData = {
  totalRows: number
  validRows: number
  skippedKeyOnly?: number // 수용가번호만 공란
  skippedNoKey?: number // 일반 키 없음
  skippedBlankField?: number // 필수 공란
  skippedDuplicate?: number // 파일 내 중복
  missingInDb?: number // DB 미존재 키 개수
  dbDuplicateCount?: number // DB 중복 키 개수
  invalidRows?: InvalidRow[]
  missingKeys?: string[] // DB에 없는 키들
  dbDuplicateKeys?: string[] // DB 중복 키들
}

interface UploadValidationModalProps {
  open: boolean
  onClose: () => void
  data: ValidationData
  onProceedPartial?: () => Promise<void> // strict=0 저장 실행
}

type IssueFilter =
  | 'ALL'
  | '수용가번호 공란'
  | '수용가번호 없음'
  | '공란'
  | '파일 내 중복 수용가번호'
  | '미등록 수용가'
  | '중복 수용가'

// XLSX 다운로드 (수용가번호 열을 문자열로 강제)
const downloadXLSX = (rows: InvalidRow[]) => {
  const aoa = [
    ['rowNumber', 'customerKey', 'issues', 'original'],
    ...rows.map((r) => {
      const original = r.original
        ? Object.entries(r.original)
            .map(([k, v]) => `${k}:${v}`)
            .join(' | ')
        : ''
      return [r.rowNumber ?? '', r.key ?? '', r.issues.join(' | '), original]
    }),
  ]

  const ws = XLSX.utils.aoa_to_sheet(aoa)

  // B열(수용가번호) 텍스트 고정 + 포맷
  const range = XLSX.utils.decode_range(ws['!ref']!)
  for (let R = 1; R <= range.e.r; ++R) {
    const addr = XLSX.utils.encode_cell({r: R, c: 1}) // 0:A, 1:B
    const cell = ws[addr]
    if (cell && cell.v != null) {
      cell.t = 's' // 문자열 타입
      ;(cell as any).z = '@' // 텍스트 서식
    }
  }

  // 여백/컬럼 너비 약간 지정(선택)
  ws['!cols'] = [{wch: 10}, {wch: 22}, {wch: 40}, {wch: 60}]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'validation_issues')

  const wbout = XLSX.write(wb, {bookType: 'xlsx', type: 'array'})
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'upload_validation_issues.xlsx'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/* ───────── 컴포넌트 ───────── */

export default function UploadValidationModal({
  open,
  onClose,
  data,
  onProceedPartial,
}: UploadValidationModalProps) {
  const [query, setQuery] = useState('')
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('ALL')
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) {
      setQuery('')
      setIssueFilter('ALL')
      setPage(1)
    }
  }, [open])

  // invalidRows + missingKeys + dbDuplicateKeys → 하나로 합치기
  const allRows: InvalidRow[] = useMemo(() => {
    const base = [...(data.invalidRows ?? [])]

    ;(data.missingKeys ?? []).forEach((k) => {
      base.push({rowNumber: 0, key: k, issues: ['DB 미존재 키']})
    })
    ;(data.dbDuplicateKeys ?? []).forEach((k) => {
      base.push({rowNumber: 0, key: k, issues: ['DB 중복 키']})
    })

    return base
  }, [data])

  // 검색/필터
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return allRows.filter((r) => {
      const matchQuery =
        q.length === 0 ||
        (r.key ?? '').toLowerCase().includes(q) ||
        r.issues.join(' ').toLowerCase().includes(q) ||
        (r.original
          ? Object.entries(r.original)
              .map(([k, v]) => `${k}:${v}`)
              .join(' ')
              .toLowerCase()
              .includes(q)
          : false)

      const matchIssue =
        issueFilter === 'ALL' ||
        r.issues.some((iss) => {
          if (issueFilter === '공란') return iss.startsWith('공란')
          return iss === issueFilter
        })

      return matchQuery && matchIssue
    })
  }, [allRows, query, issueFilter])

  // 페이징
  const pageSize = 20
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize)

  const proceedPartial = async () => {
    if (!onProceedPartial) return
    try {
      setBusy(true)
      await onProceedPartial()
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className='fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4'>
      <div className='w-full max-w-6xl bg-white rounded-2xl shadow-xl overflow-hidden'>
        {/* Header */}
        <div className='px-6 py-4 border-b flex items-center justify-between'>
          <div>
            <h2 className='text-xl font-semibold'>업로드 검증 결과</h2>
            <p className='text-sm text-gray-500'>
              총 {data.totalRows}건 중 유효 {data.validRows}건
            </p>
          </div>
          <button
            onClick={onClose}
            className='rounded-lg px-3 py-2 text-sm border hover:bg-gray-50'
          >
            닫기
          </button>
        </div>

        {/* Stats */}
        <div className='px-6 py-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-sm'>
          <Stat label='수번 공란' value={data.skippedKeyOnly ?? 0} />
          <Stat label='수번 없음' value={data.skippedNoKey ?? 0} />
          <Stat label='공란' value={data.skippedBlankField ?? 0} />
          <Stat label='파일 내 중복 수용가번호' value={data.skippedDuplicate ?? 0} />
          <Stat label='미등록 수용가' value={data.missingInDb ?? 0} />
          <Stat label='중복 수용가' value={data.dbDuplicateCount ?? 0} />
        </div>

        {/* Controls */}
        <div className='px-6 pb-3 flex flex-col md:flex-row gap-2 md:items-center md:justify-between'>
          <div className='flex gap-2'>
            <select
              className='border rounded-md px-3 py-2 text-sm'
              value={issueFilter}
              onChange={(e) => {
                setIssueFilter(e.target.value as IssueFilter)
                setPage(1)
              }}
            >
              <option value='ALL'>전체</option>
              <option value='수번 공란'>수용가번호 공란</option>
              <option value='수번 없음'>수용가번호 없음</option>
              <option value='공란'>공란</option>
              <option value='파일 내 중복 수용가번호'>파일 내 중복 수용가번호</option>
              <option value='미등록 수용가'>미등록 수용가</option>
              <option value='중복 수용가'>중복 수용가</option>
            </select>

            <input
              className='border rounded-md px-3 py-2 text-sm w-64'
              placeholder='수용가번호/이슈/원본값 검색'
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setPage(1)
              }}
            />
          </div>

          <div className='flex gap-2'>
            <button
              onClick={() => downloadXLSX(filtered)}
              className='px-3 py-2 text-sm rounded-md border hover:bg-gray-50'
            >
              엑셀(.xlsx) 다운로드
            </button>
            {onProceedPartial && (
              <button
                onClick={proceedPartial}
                disabled={busy}
                className={`px-3 py-2 text-sm rounded-md text-white ${
                  busy ? 'bg-indigo-300' : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {busy ? '저장 중...' : '정상 행만 저장'}
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className='px-6 pb-2 overflow-auto'>
          <table className='w-full text-sm border border-gray-200 rounded-lg'>
            <thead className='bg-gray-50 sticky top-0'>
              <tr>
                <Th>행 번호</Th>
                <Th>수용가번호</Th>
                <Th>오류</Th>
                <Th>원본 값(요약)</Th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className='text-center p-6 text-gray-500'>
                    표시할 데이터가 없습니다.
                  </td>
                </tr>
              ) : (
                pageRows.map((r, i) => (
                  <tr key={`${r.key}-${i}`} className='border-t'>
                    <Td>{r.rowNumber || '-'}</Td>
                    <Td className='font-medium'>{r.key ?? '-'}</Td>
                    <Td>
                      <div className='flex flex-wrap gap-1'>
                        {r.issues.map((iss, idx) => (
                          <span
                            key={idx}
                            className='inline-block px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200'
                          >
                            {iss}
                          </span>
                        ))}
                      </div>
                    </Td>
                    <Td className='truncate max-w-[520px]'>
                      {r.original
                        ? Object.entries(r.original)
                            .map(([k, v]) => `${k}:${v}`)
                            .join(' | ')
                        : '-'}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className='px-6 py-3 flex items-center justify-between border-t'>
          <span className='text-xs text-gray-500'>
            {filtered.length}건 중 {pageRows.length}건 표시 · {page}/{totalPages} 페이지
          </span>
          <div className='flex gap-2'>
            <button
              className='px-3 py-1 text-sm rounded-md border hover:bg-gray-50 disabled:opacity-50'
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              이전
            </button>
            <button
              className='px-3 py-1 text-sm rounded-md border hover:bg-gray-50 disabled:opacity-50'
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ───────── 작은 컴포넌트 ───────── */
function Stat({label, value}: {label: string; value: number}) {
  return (
    <div className='rounded-xl border p-3 flex items-center justify-between'>
      <span className='text-gray-500'>{label}</span>
      <span className='font-semibold'>{value}</span>
    </div>
  )
}
function Th({children}: {children: React.ReactNode}) {
  return <th className='text-left font-semibold text-gray-700 px-3 py-2 border-b'>{children}</th>
}
function Td({children, className = ''}: {children: React.ReactNode; className?: string}) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>
}
